import type React from 'react';

import { buttonConfig } from './button';
import type { ButtonProps } from './button';
import { headingConfig } from './heading';
import type { HeadingProps } from './heading';
import { paragraphConfig } from './paragraph';
import type { ParagraphProps } from './paragraph';
import { imageConfig } from './image';
import type { ImageProps } from './image';

/**
 * Props Puck passes to the slot component it injects into a container block's
 * `content` prop. Shared by every container block (Section, FlexLayout, ...).
 *
 * `className` and `as` are part of Puck's `DropZoneProps` (verified against
 * `@puckeditor/core` 0.23.0 types). Both are optional because Section and other callers
 * that do not need them can ignore them without TypeScript errors.
 */
export type SlotProps = {
  style?: React.CSSProperties;
  minEmptyHeight?: number | string;
  className?: string;
  as?: React.ElementType;
  allow?: string[];
};

/**
 * A slot child, used to author starter-layout defaults.
 *
 * `props` is the child's **complete** prop set, not a `Partial`. Puck merges a component's
 * `defaultProps` only for the component being inserted (`insertAction`, `@puckeditor/core`
 * dist/index.js:2035) — nothing merges defaults into that component's slot *children*. A child
 * written here with only some of its props therefore reaches the editor genuinely incomplete,
 * which breaks two things:
 *
 *   - The field panel reads stored props **unmerged** (`FieldsChild` -> `getCurrentData().props`,
 *     dist/index.js:12513 and 3469), while the canvas separately merges `defaultProps` for
 *     rendering (dist/index.js:10767). An absent prop shows an empty/unselected control next to a
 *     preview that looks correct.
 *   - For an `object` field, Puck reads sub-fields by path and `ObjectField.getValue()` falls back
 *     to `{}`, then merges only the edited key (dist/index.js:7977, 8002). Editing one padding side
 *     of a child whose `padding` is absent stores `{ top: '8px' }`, and `spacingToCss` renders that
 *     as `8px undefined undefined undefined` — the author's edit silently does nothing.
 *
 * This is the same invariant `editor/default-layout-parity.test.tsx` guards for default-layout
 * data (ECOMM-13011), applied to slot defaults. Use `starterChild()` to satisfy it structurally
 * rather than by hand-copying props.
 *
 * `id` is deliberately excluded from the type. Puck's `populateIds()` stamps a fresh id via
 * `{ id: generatedId, ...item.props }`, so a hardcoded `id` in `props` would overwrite the
 * generated one — producing duplicate ids across every inserted instance and silently corrupting
 * `state.indexes.nodes`. Omitting `id` lets `populateIds` always win.
 *
 * The union keyed on `type` means TypeScript checks each child's props against the actual
 * component type. A typo like `lable` on a Button child is a compile-time error (TS2353) rather
 * than a silent wrong-default at runtime.
 */
export type StarterChild =
  | { type: 'Button'; props: Omit<ButtonProps, 'id'> }
  | { type: 'Heading'; props: Omit<HeadingProps, 'id'> }
  | { type: 'Image'; props: ImageProps }
  | { type: 'Paragraph'; props: Omit<ParagraphProps, 'id'> };

/** The child block types a starter layout may use, mapped to their configs. */
const STARTER_CHILD_CONFIGS = {
  Button: buttonConfig,
  Heading: headingConfig,
  Paragraph: paragraphConfig,
  Image: imageConfig,
} as const;

type StarterChildType = StarterChild['type'];

/** The props type for a given starter child type, as its union member declares it. */
type StarterPropsFor<T extends StarterChildType> = Extract<StarterChild, { type: T }>['props'];

/**
 * Builds a starter-layout child with its component's full `defaultProps` as the base, overridden
 * by `overrides`.
 *
 * Deriving from the component's own `defaultProps` rather than hand-listing props is what keeps
 * this correct over time: adding a field to a supported child flows into every starter
 * layout automatically, instead of leaving a silently-absent prop behind. See `StarterChild` for
 * why an absent prop is a real defect rather than a cosmetic one.
 *
 * `id` is stripped rather than merely omitted, so a config that ever gains an `id` default cannot
 * reintroduce the duplicate-id corruption `populateIds()` protects against.
 */
export function starterChild<T extends StarterChildType>(
  type: T,
  overrides: Partial<StarterPropsFor<T>> = {}
): Extract<StarterChild, { type: T }> {
  const defaults = STARTER_CHILD_CONFIGS[type].defaultProps ?? {};
  const { id: _id, ...defaultsWithoutId } = defaults as Record<string, unknown>;

  // Returns the specific union member for `type` rather than the whole union, so a caller reading
  // e.g. `starterChild('Button', ...).props.label` type-checks without narrowing by hand.
  return ({
    type,
    props: { ...defaultsWithoutId, ...overrides },
  } as unknown) as Extract<StarterChild, { type: T }>;
}
